import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, Sun, Moon, Languages, Shield, LogOut } from "lucide-react";

const Header = () => {
    const { t, lang, toggleLang, theme, toggleTheme } = useApp();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const nav = useNavigate();

    const links = [
        { to: "/", label: t.nav.home, tid: "nav-home" },
        { to: "/about", label: t.nav.about, tid: "nav-about" },
        { to: "/services", label: t.nav.services, tid: "nav-services" },
        { to: "/forms", label: t.nav.forms, tid: "nav-forms" },
        { to: "/news", label: t.nav.news, tid: "nav-news" },
        { to: "/booking", label: t.nav.booking, tid: "nav-booking" },
        { to: "/track", label: t.nav.track, tid: "nav-track" },
        { to: "/contact", label: t.nav.contact, tid: "nav-contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 glass border-b border-white/40 dark:border-white/10"
        >
            <div className="container-x flex items-center justify-between h-16 sm:h-20">
                <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
                    <img src="/logo.webp" alt="Sri Matha Multiservices"
                        className="w-12 h-12 sm:w-14 sm:h-14 object-contain drop-shadow-sm transition-transform duration-300 group-hover:rotate-[8deg]" />
                    <div className="hidden sm:block leading-tight">
                        <div className="text-[15px] font-bold tracking-tight text-foreground">Sri Matha</div>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Multiservices</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={l.tid}
                            className={({ isActive }) =>
                                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                                    isActive ? "text-white bg-[hsl(var(--royal))]" : "text-foreground hover:text-[hsl(var(--royal))] hover:bg-[hsl(var(--muted))]"
                                }`
                            }>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-1 sm:gap-2">
                    <button data-testid="lang-toggle" onClick={toggleLang}
                        className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                        aria-label="Toggle language">
                        <Languages size={18} />
                        <span className="sr-only">{lang === "en" ? "TE" : "EN"}</span>
                    </button>
                    <button data-testid="theme-toggle" onClick={toggleTheme}
                        className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                        aria-label="Toggle theme">
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                    {user && user !== false && user.role === "admin" ? (
                        <div className="hidden sm:flex items-center gap-2">
                            <button data-testid="dashboard-btn" onClick={() => nav("/admin")}
                                className="btn-royal !py-2 !px-4 !text-xs">
                                <Shield size={14} />
                                {t.nav.dashboard}
                            </button>
                            <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
                                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-muted" aria-label="Logout">
                                <LogOut size={18} />
                            </button>
                        </div>
                    ) : null}

                    <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
                        className="lg:hidden h-10 w-10 rounded-full flex items-center justify-center hover:bg-muted">
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden border-t border-border bg-background/95 backdrop-blur-lg">
                    <div className="container-x py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                                        isActive ? "bg-[hsl(var(--royal))] text-white" : "hover:bg-muted"
                                    }`
                                }>
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="h-px my-2 bg-border" />
                        {user && user !== false && user.role === "admin" ? (
                            <>
                                <button onClick={() => { setOpen(false); nav("/admin"); }}
                                    className="btn-royal justify-center">{t.nav.dashboard}</button>
                                <button onClick={() => { setOpen(false); logout(); nav("/"); }}
                                    className="btn-ghost-royal justify-center mt-2">{t.nav.logout}</button>
                            </>
                        ) : (
                            <Link to="/admin/login" onClick={() => setOpen(false)} className="btn-ghost-royal justify-center">{t.nav.adminLogin}</Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
